import React, {useEffect, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {fetchTranslations} from "../types/translationsClients";
import ContactComponent from "../components/ContactComponent";
import ParticleMorphStory, {StoryPanel} from "../components/ParticleMorphStory";
import {Helmet} from 'react-helmet-async';

const Landing = () => {
    const location = useLocation();
    const [, setDict] = useState<Record<string, string> | null>(null);
    const locale = "de";
    const namespace = "landing";

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchTranslations({
                    baseUrl: process.env.REACT_APP_API_BASE_URL || "",
                    locale,
                    namespace
                });
                if (!cancelled) setDict(data.items);
            } catch (e) {
                if (!cancelled) setDict({});
                console.error("Failed to load translations (landing)", e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Jump to the contact section when a CTA elsewhere on the site sent us here
    useEffect(() => {
        const state = location.state as { scrollTo?: string } | null;
        if (!state?.scrollTo) return;
        const id = state.scrollTo;
        const timer = window.setTimeout(() => {
            const el = document.getElementById(id);
            if (!el) return;
            const doc = document.documentElement;
            const prev = doc.style.scrollBehavior;
            doc.style.scrollBehavior = 'auto';
            el.scrollIntoView({block: 'center'});
            doc.style.scrollBehavior = prev;
        }, 250);
        // Clear the state so refresh / back-navigation doesn't re-scroll
        window.history.replaceState({}, document.title);
        return () => window.clearTimeout(timer);
    }, [location.state]);

    // Reveal-on-scroll for story cards and content sections
    useEffect(() => {
        const els = document.querySelectorAll('.morph-panel__card, .reveal');
        if (!els.length) return;
        const io = new IntersectionObserver(
            entries => entries.forEach(en => {
                if (en.isIntersecting) en.target.classList.add('is-visible');
                else en.target.classList.remove('is-visible');
            }),
            {threshold: 0.35}
        );
        els.forEach(el => io.observe(el));
        return () => io.disconnect();
    }, []);

    const storyPanels: StoryPanel[] = [
        // 0 — Hero (particles form the Dockline logo)
        {
            content: (
                <div className="morph-panel__hero" key="hero">
                    <h1>Dockline Digital</h1>
                    <h2>All Things Digital — One Dockline.</h2>
                    <button
                        className="morph-panel__scroll-hint"
                        onClick={() => window.scrollTo({top: window.innerHeight, behavior: 'smooth'})}
                    >
                        Scroll to Explore <i className="bi bi-chevron-double-down"></i>
                    </button>
                </div>
            )
        },

        // 1 — Intro (particles disperse into a cloud)
        {
            content: (
                <div className="morph-panel__card morph-panel__card--center" key="intro">
                    <h2>Three Divisions. One Line.</h2>
                    <p>Technology, creativity, and reliability — brought together in one partner.
                        Keep scrolling and watch it take shape.</p>
                </div>
            )
        },

        // 2 — Systems (particles form a server)
        {
            snap: true,
            content: (
                <div className="morph-panel__card" key="systems">
                    <h3 className="systems-color">Dockline Systems</h3>
                    <p>We provide reliable <strong>IT Administration and Technical Support</strong>, ensuring
                        that infrastructure, networks, and systems run securely and efficiently for businesses
                        of any size.</p>
                    <button id="sys-learn-more-button"><Link to="/systems">Learn more →</Link></button>
                </div>
            )
        },

        // 3 — Development (particles form </>)
        {
            snap: true,
            content: (
                <div className="morph-panel__card" key="dev">
                    <h3 className="dev-color">Dockline Development</h3>
                    <p>Our <strong>Software Engineering</strong> division designs and builds scalable web and
                        mobile solutions, combining clean code with modern frameworks to deliver
                        high‑performance digital products.</p>
                    <button id="dev-learn-more-button"><Link to="/dev">Learn more →</Link></button>
                </div>
            )
        },

        // 4 — Studios (particles form an aperture)
        {
            snap: true,
            content: (
                <div className="morph-panel__card" key="studios">
                    <h3 className="studio-color">Dockline Studios</h3>
                    <p>Dockline Studios specializes in <strong>Photography, Videography</strong>,
                        and <strong>Post‑Production</strong>. We craft visual content that strengthens brand
                        identity and engages audiences across all media.</p>
                    <button id="stud-learn-more-button"><Link to="/studios">Learn more →</Link></button>
                </div>
            )
        },

        // 5 — Outro (particles reassemble the logo)
        {
            content: (
                <div className="morph-panel__card morph-panel__card--center morph-panel__card--outro" key="outro">
                    <h2>One Partner. All Things Digital.</h2>
                    <p>Systems, Development, and Studios — three specialties, one dockline to your goals.</p>
                    <button
                        id="outro-contact-button"
                        onClick={() => document.getElementById('contact-section')
                            ?.scrollIntoView({behavior: 'smooth', block: 'center'})}
                    >
                        Start a project →
                    </button>
                </div>
            )
        }
    ];

    return (
        <>
            <Helmet>
                <title>Dockline Digital | IT-Service, Webentwicklung &amp; Medienproduktion in München</title>
                <meta
                    name="description"
                    content="Dockline Digital bietet IT-Administration, Webentwicklung und Foto-/Videoproduktion in München und ganz Deutschland. Ein Partner für Systeme, Software und Studios."
                />
                <meta
                    name="keywords"
                    content="IT Administrator München, IT Service München, IT Betreuung München, Webentwicklung München, Softwareentwicklung München, Web Developer München, Fotograf München, Videoproduktion München, Dockline Digital"
                />
                <link rel="canonical" href="https://dockline.digital/"/>

                {/* Open Graph meta tags */}
                <meta property="og:type" content="website"/>
                <meta property="og:title" content="Dockline Digital – IT, Development &amp; Studios in München"/>
                <meta
                    property="og:description"
                    content="IT-Administration, Webentwicklung und Medienproduktion aus einer Hand. Dockline Digital – Ihr Digitalpartner in München."
                />
                <meta property="og:url" content="https://dockline.digital/"/>
                <meta property="og:image" content="https://dockline.digital/pics/og/landing-og.jpg"/>
                <meta property="og:locale" content="de_DE"/>

                {/* Basic organization + local business schema for Dockline Digital */}
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "Organization",
                  "name": "Dockline Digital",
                  "url": "https://dockline.digital/",
                  "logo": "https://dockline.digital/pics/logo/sec/dd_logo_colors_sec.svg",
                  "sameAs": [],
                  "description": "Dockline Digital bietet IT-Administration, Webentwicklung sowie Foto- und Videoproduktion für Unternehmen in München und Deutschland.",
                  "address": {
                    "@type": "PostalAddress",
                    "addressLocality": "München",
                    "addressCountry": "DE"
                  }
                }
                `}</script>

                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "LocalBusiness",
                  "name": "Dockline Digital",
                  "image": "https://dockline.digital/pics/og/landing-og.jpg",
                  "url": "https://dockline.digital/",
                  "telephone": "+49-152-53819840",
                  "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "Bauerstraße 26",
                    "postalCode": "80796",
                    "addressLocality": "München",
                    "addressCountry": "DE"
                  },
                  "geo": {
                    "@type": "GeoCoordinates",
                    "latitude": 48.137154,
                    "longitude": 11.576124
                  },
                  "areaServed": [
                    {
                      "@type": "City",
                      "name": "München"
                    },
                    {
                      "@type": "Country",
                      "name": "Deutschland"
                    }
                  ]
                }
                `}</script>
            </Helmet>

            <div id="landing-v2">
                <ParticleMorphStory panels={storyPanels}/>

                <div className="landing-content">
                    <section className="landing-cta-container reveal">
                        <ContactComponent/>
                    </section>

                    <section className="munich-services reveal" aria-labelledby="munich-services-title">
                        <h2 id="munich-services-title">Vor Ort in München</h2>
                        <p className="munich-services__lead">
                            Ob <strong>IT-Administrator</strong>, <strong>Webentwickler</strong> oder{' '}
                            <strong>Fotograf</strong> — auf unseren lokalen Seiten finden Sie mehr zu unseren
                            Leistungen in München und der Region.
                        </p>
                        <ul className="munich-services__links">
                            <li>
                                <Link className="munich-link munich-link--systems" to="/it-administrator-muenchen">
                                    <i className="bi bi-hdd-network"></i> IT-Administrator in München
                                </Link>
                            </li>
                            <li>
                                <Link className="munich-link munich-link--dev" to="/webentwickler-muenchen">
                                    <i className="bi bi-code-slash"></i> Webentwickler in München
                                </Link>
                            </li>
                            <li>
                                <Link className="munich-link munich-link--studios" to="/fotograf-muenchen">
                                    <i className="bi bi-camera"></i> Fotograf in München
                                </Link>
                            </li>
                        </ul>
                    </section>
                </div>
            </div>
        </>
    );
};
export default Landing;
